import React, { useState } from "react";
import { Minus, Plus, ChevronRight } from "lucide-react";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";

export default function MealCard({ meal, qty, onQtyChange, onImageClick }) {
    const [open, setOpen] = useState(false);
    const clickable = typeof onImageClick === "function";

    return (
        <article
            data-testid={`meal-card-${meal.id}`}
            className="group bg-white rounded-[20px] overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.07)] hover:shadow-[0_22px_56px_rgba(0,0,0,0.15)] transition-all duration-300 ease-out hover:-translate-y-1"
        >
            {/* Image — clickable to open full menu */}
            <div
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? onImageClick : undefined}
                onKeyDown={
                    clickable
                        ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  onImageClick();
                              }
                          }
                        : undefined
                }
                aria-label={
                    clickable ? `Open full menu — ${meal.name}` : undefined
                }
                data-testid={`meal-${meal.id}-image-trigger`}
                className={`relative h-[220px] overflow-hidden ${clickable ? "cursor-pointer focus:outline-none focus:ring-4 focus:ring-gold/40" : ""}`}
            >
                <img
                    src={meal.image}
                    alt={meal.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                {/* Diet tag */}
                <span
                    data-testid={`meal-${meal.id}-diet-tag`}
                    className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-wider uppercase text-white shadow-md"
                    style={{ background: meal.diet_tag_color }}
                >
                    {meal.diet_tag}
                </span>
                {/* Cuisine emoji circle */}
                <span className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center text-xl shadow-md">
                    {meal.cuisine_emoji}
                </span>
                {/* Bottom gradient + title */}
                <div className="meal-card-img-overlay absolute inset-0" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-serif font-black text-white text-2xl sm:text-[26px] leading-tight">
                        {meal.name}
                    </h3>
                    <p className="text-white/75 text-sm mt-1">
                        {meal.subtitle}
                    </p>
                    {clickable && (
                        <p className="mt-2 inline-flex items-center gap-1 text-gold text-[11px] font-bold tracking-widest uppercase">
                            Tap to see full menu →
                        </p>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="p-6">
                <p className="text-[15px] leading-relaxed text-muted2">
                    {meal.description}
                </p>

                {/* Nutrition strip */}
                <div className="mt-5 grid grid-cols-4 gap-2 bg-[#F7F5EF] rounded-2xl p-3">
                    <NutritionCell
                        icon="🔥"
                        value={meal.nutrition.calories}
                        label="Cal"
                        color="text-burnt"
                    />
                    <NutritionCell
                        icon="💪"
                        value={`${meal.nutrition.protein}g`}
                        label="Protein"
                    />
                    <NutritionCell
                        icon="🍚"
                        value={`${meal.nutrition.carbs}g`}
                        label="Carbs"
                    />
                    <NutritionCell
                        icon="🫒"
                        value={`${meal.nutrition.fat}g`}
                        label="Fat"
                    />
                </div>

                {/* Ingredients collapsible */}
                <Collapsible open={open} onOpenChange={setOpen} className="mt-5">
                    <CollapsibleTrigger asChild>
                        <button
                            data-testid={`meal-${meal.id}-ingredients-toggle`}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-full bg-mint hover:bg-mint/70 transition-colors text-forest font-medium text-sm"
                        >
                            <span className="flex items-center gap-2">
                                <span>🌿</span> What's inside this meal?
                            </span>
                            <ChevronRight
                                size={18}
                                className={`transition-transform duration-300 ${open ? "rotate-90" : ""}`}
                            />
                        </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                        <div className="pt-4 flex flex-wrap gap-2">
                            {meal.ingredients.map((ing, i) => (
                                <span
                                    key={i}
                                    className="px-3 py-1.5 rounded-full bg-forest/10 text-forest text-[13px] font-medium"
                                >
                                    {ing}
                                </span>
                            ))}
                        </div>
                    </CollapsibleContent>
                </Collapsible>

                {(meal.effective_min_quantity || 1) > 1 && (
                    <div
                        data-testid={`meal-${meal.id}-min-note`}
                        className="mt-4 px-3 py-2 rounded-xl bg-burnt/10 border border-burnt/30 text-burnt text-xs font-bold tracking-wider"
                    >
                        {meal.is_premium ? "⭐ Premium" : "📋 Off-menu"} — minimum{" "}
                        {meal.effective_min_quantity} per order
                    </div>
                )}

                {/* Price + stepper */}
                <div className="mt-6 flex items-end justify-between">
                    <div>
                        <p className="text-[11px] uppercase tracking-widest text-muted2 font-bold flex items-center gap-2">
                            Per Meal
                            {meal.is_premium ? (
                                <span className="px-2 py-0.5 rounded-full bg-navy text-gold text-[9px] font-bold uppercase tracking-widest">
                                    Premium
                                </span>
                            ) : meal.original_price &&
                              meal.original_price > meal.price ? (
                                <span className="px-2 py-0.5 rounded-full bg-burnt text-white text-[9px] font-bold uppercase tracking-widest">
                                    Launch Deal
                                </span>
                            ) : null}
                        </p>
                        <div className="flex items-baseline gap-2.5">
                            <p
                                data-testid={`meal-${meal.id}-price`}
                                className="font-display text-forest text-5xl leading-none tracking-wide"
                            >
                                ${meal.price.toFixed(2)}
                            </p>
                            {!meal.is_premium &&
                                meal.original_price &&
                                meal.original_price > meal.price && (
                                    <p
                                        data-testid={`meal-${meal.id}-original-price`}
                                        className="font-display text-muted2 text-2xl leading-none line-through decoration-2 decoration-burnt/70"
                                    >
                                        ${meal.original_price.toFixed(2)}
                                    </p>
                                )}
                        </div>
                    </div>
                    <QtyStepper
                        qty={qty}
                        onChange={onQtyChange}
                        testId={meal.id}
                        meal={meal}
                    />
                </div>
            </div>
        </article>
    );
}

function NutritionCell({ icon, value, label, color = "text-navy" }) {
    return (
        <div className="text-center">
            <div className="text-base leading-none">{icon}</div>
            <div className={`mt-1 font-bold text-[15px] ${color}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted2">
                {label}
            </div>
        </div>
    );
}

function QtyStepper({ qty, onChange, testId, meal }) {
    const minQty = Math.max(meal?.effective_min_quantity || 1, 1);
    const handleIncrement = () => {
        if (qty === 0 && minQty > 1) {
            onChange(minQty);
        } else {
            onChange(qty + 1);
        }
    };
    const handleDecrement = () => {
        if (qty <= minQty) {
            onChange(0);
        } else {
            onChange(qty - 1);
        }
    };
    return (
        <div className="inline-flex items-center gap-1 bg-cream rounded-full border border-mint p-1">
            <button
                data-testid={`meal-${testId}-decrement`}
                onClick={handleDecrement}
                disabled={qty === 0}
                aria-label="decrease quantity"
                className="w-10 h-10 rounded-full flex items-center justify-center text-forest hover:bg-mint disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
                <Minus size={16} />
            </button>
            <span
                data-testid={`meal-${testId}-qty`}
                className="font-display text-2xl text-navy w-7 text-center tabular-nums"
            >
                {qty}
            </span>
            <button
                data-testid={`meal-${testId}-increment`}
                onClick={handleIncrement}
                aria-label="increase quantity"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-forest text-white hover:bg-sage transition-colors active:scale-95"
            >
                <Plus size={16} />
            </button>
        </div>
    );
}
